import { useCallback, useEffect, useState } from "react";
import { cancelCompanionLlmStream, startCompanionLlmStream } from "../services/companionGraphqlSseClient";
import { AttachmentInput } from "../graphql/generated/graphql";

type StreamCompletionEvent = { chatId?: string; messageId?: string };

interface StartStreamOptions {
  chatId?: string;
  workspaceId?: string;
  attachment?: AttachmentInput;
  onChunk?: (payload: { text: string; messageId?: string; isFinalChunk?: boolean }) => void;
  onComplete?: (payload: { event?: StreamCompletionEvent; text: string }) => void;
  onError?: (payload: { error: Error }) => void;
}

const useCompanionLlmStream = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");

  const startStream = useCallback(async (question: string, options: StartStreamOptions = {}) => {
    const { onComplete, onError, onChunk, ...streamOptions } = options;

    setIsStreaming(true);
    setStreamingText("");

    let completed = false;
    let completionEvent: StreamCompletionEvent | undefined;
    let accumulatedText = "";

    const finalize = () => {
      if (completed) {
        return;
      }
      completed = true;
      setIsStreaming(false);
      onComplete?.({ event: completionEvent, text: accumulatedText });
      setStreamingText("");
    };

    try {
      await startCompanionLlmStream(
        question,
        {
          onChunk: (event) => {
            if (!event) {
              return;
            }

            switch (event.__typename) {
              case "LLMStreamMessageDeltaEvent": {
                const deltaEvent = event;
                const delta = deltaEvent.delta;
                if (delta?.__typename === "LLMStreamTextBlock" && delta.text) {
                  accumulatedText += delta.text;
                  setStreamingText(accumulatedText);
                  onChunk?.({
                    text: delta.text,
                    messageId: deltaEvent.messageId,
                    isFinalChunk: deltaEvent.isFinalChunk ?? undefined,
                  });
                } else {
                  // console.warn("[useCompanionLlmStream] Delta event but no text", {
                  //   deltaType: delta?.__typename,
                  //   hasText: !!(delta as any)?.text,
                  // });
                }
                break;
              }
              case "LLMStreamMessageCompletedEvent": {
                completionEvent = {
                  chatId: event.message?.chatId,
                  messageId: event.message?.id,
                };
                finalize();
                break;
              }
              case "LLMStreamArtifactCreatedEvent": {
                break;
              }
              case "LLMStreamErrorEvent": {
                console.error("[useCompanionLlmStream] Error event", { errorMessage: event.errorMessage });
                const error = new Error(event.errorMessage ?? "Stream error");
                completed = true;
                cancelCompanionLlmStream();
                setIsStreaming(false);
                setStreamingText("");
                onError?.({ error });
                break;
              }
              default: {
                break;
              }
            }
          },
          onComplete: () => {
            finalize();
          },
          onError: (error) => {
            console.error("[useCompanionLlmStream] Transport error", { message: (error as Error).message });
            setIsStreaming(false);
            setStreamingText("");
            onError?.({ error });
          },
        },
        streamOptions
      );
    } catch (error) {
      console.error("[useCompanionLlmStream] Failed to start stream", { message: (error as Error).message });
      setIsStreaming(false);
      setStreamingText("");
      const typedError = error as Error;
      onError?.({ error: typedError });
      throw error;
    }
  }, []);

  const cancelStream = useCallback(() => {
    cancelCompanionLlmStream();
    setIsStreaming(false);
    setStreamingText("");
  }, []);

  useEffect(() => cancelStream, [cancelStream]);

  return { startStream, cancelStream, isStreaming, streamingText };
};

export default useCompanionLlmStream;
